import {computed, ref, watch} from 'vue';
import {useMonitorLogging} from '@/core/useMonitor/logging';
import {useDocumentVisibility, useEventListener, useFullscreen, useTimeoutFn, useWindowFocus,} from '@vueuse/core';
import store from "@/store";
import global_methods from "@/utils/globalFunctions";


export const MonitorBrowserError = {
    CURSOR_LOST: 'cursor_lost',
    FOCUS_LOST: 'focus_lost',
    TAB_LOST: 'tab_lost',
    FULL_SCREEN_LOST: 'full_screen_lost',
    FULL_SCREEN_ERROR: 'full_screen_error',
};

/**
 * Options defined as vanilla object until we can support TypeScript interfaces.
 */
export const UseMonitorBrowserOptions = {
    // Callback invoked when an error occurs and test is halted
    onError: null,
    // Enable fullscreen monitoring
    fullscreenEnabled: true,
    // Delay in milliseconds after entering fullscreen and monitoring is enabled
    fullscreenDelayAfterEnter: 1000,
    // Delay in milliseconds before checking window dimensions whilst in fullscreen
    fullscreenDelayBeforeResize: 300,
    // Delay in milliseconds after determining window dimensions changed whilst in fullscreen
    fullscreenDelayAfterResize: 1000,
    // Enable window focus monitoring
    focusedEnabled: true,
    // Enable document visibility monitoring
    visibleEnabled: true,
    // Enable mouse outside monitoring
    outsideEnabled: true,
    // Delay in seconds before halting when losing focus
    focusedDelay: 3,
    // Alternative delay in seconds before halting after a losing focus error
    focusedDelayAfterError: 2,
    // Whether to reset the timeout when focus is returned
    focusedTimeoutReset: false,
    // Delay in seconds before halting when mouse is outside
    outsideDelay: 3,
    // Alternative delay in seconds before halting after a mouse is outside error
    outsideDelayAfterError: 2,
    // Whether to reset the timeout when mouse is returned
    outsideTimeoutReset: false,
}

/**
 * Reactively monitor various browser events.
 *
 * @param options
 */
export function useMonitorBrowser(options = {}) {
    options = {...UseMonitorBrowserOptions, ...options};

    const fullscreenEnabled = ref(options.fullscreenEnabled);
    const focusedEnabled = ref(options.focusedEnabled);
    const visibleEnabled = ref(options.visibleEnabled);
    const outsideEnabled = ref(options.outsideEnabled);

    const focusedDelay = ref(options.focusedDelay);
    const focusedTimeout = ref(0);
    const outsideDelay = ref(options.outsideDelay);
    const outsideTimeout = ref(0);

    const windowSize = ref({width: 0, height: 0});
    const originalWindowSize = ref({width: 0, height: 0});
    const fullscreenSize = ref({width: 0, height: 0});

    const isMonitoring = ref(false);
    const isHalted = computed(()=>store.getters['Monitoring/isMonitoringHalted']);
    const isVisible = ref(true);
    const isFocused = useWindowFocus();
    const isOutside = ref(false);
    const visibility = useDocumentVisibility();
    const isFullScreenModeStarting = ref(false);

    const mouseLocation = ref({x: null, y: null});

    if (global_methods.isFirefox()) {
        // adjust timing for firefox
        options.fullscreenDelayBeforeResize = 1000;
    }

    const {
        isSupported: isFullscreenSupported,
        isFullscreen: isFullscreen,
        enter: enterFullscreen,
        exit: exitFullscreen,
        toggle: toggleFullscreen,
    } = useFullscreen();

    const {
        browserEvents,
        logCursorLost,
        logFocusLost,
        logTabLost,
        logFullScreenLost,
    } = useMonitorLogging();

    let focusedCount = 0;
    let outsideCount = 0;
    let windowResizeTimeout = null;

    const isMobileDevice = store.getters.isMobileDevice;
    let clickedOnIframeContents = false;

    watch(isFullscreen, (current) => {
        if (!isMonitoring.value || !fullscreenEnabled.value || isHalted.value) {
            return;
        }

        if (!current) {
            haltWithError(MonitorBrowserError.FULL_SCREEN_LOST);

            logFullScreenLost().then(() => {
            });
        }
    }, {immediate: true});

    watch(visibility, (current) => {

        if (!isMonitoring.value || !visibleEnabled.value || isHalted.value || isFullScreenModeStarting.value) {
            return;
        }

        isVisible.value = current.toString() === 'visible';


        if (!isVisible.value) {
            // console.debug('before tab lost: ', {isMonitoring, visibleEnabled})
            haltWithError(MonitorBrowserError.TAB_LOST);

            logTabLost().then(() => {
            });
        }
    }, {immediate: true});


    if (focusedEnabled.value) {
        watch(isFocused, (current) => {

            if (current === false) {
                // console.log('out focused');

                /**
                 when mouse outside event detector enabled,
                 then we need to check if the window lost tab and the mouse is outside of the window
                 this will prevent false positive when playing video and audios.

                 We have sound cloud play issue on Galaxy Tab. This is mainly because
                 when a user click play button on sound cloud player, the focus remains inside
                 of sound cloud browser
                 */

                if (isMobileDevice && outsideEnabled.value === true) { // mobile device
                    if (isOutside.value === true) {
                        maybeHaltOnFocused();
                    }
                } else {

                    // if the mouse is still inside of the current window, we need to check the element in iframe.
                    if (isOutside.value === false) {

                        // if we clicked on iframe we skip this validation
                        if (clickedOnIframeContents === true) {
                            // console.debug('still in the iframe')
                            return;
                        } else {

                            // Otherwise we need to check the element in iframe.
                            const elementOnMouse = document.elementFromPoint(mouseLocation.value.x, mouseLocation.value.y);
                            let isIframe = elementOnMouse instanceof HTMLIFrameElement || elementOnMouse instanceof HTMLEmbedElement;

                            // if mouse cursor located in the iframe, we set clickedOnIframeContents to be true
                            if (isIframe) {
                                // console.debug('click inside of iframe')
                                clickedOnIframeContents = true;
                                return;
                            }
                        }
                    }
                    maybeHaltOnFocused();
                }
            }
        });
    }

    useEventListener(document, 'mousemove', (event) => {
        mouseLocation.value.x = event.clientX
        mouseLocation.value.y = event.clientY
    })


    useEventListener(document, 'mouseleave', () => {
        isOutside.value = true;
        // console.log('mouse leave detected');
        maybeHaltOnOutside();
    })

    useEventListener(document, 'mouseover', () => {
        isOutside.value = false;

        if (clickedOnIframeContents === true) {
            // console.log('back from iframe');
            clickedOnIframeContents = false;
            document.body.focus();
        }
    })


    /**
     * MacOS doesn't trigger fullscreen exits event when clicking "exit full screen" for the browser.
     * We need to check resize event if the resolution has been changed by that.
     * However, Firefox also trigger resize event for zoom in and out
     *
     * By doing that we can ingore zoom event successfully
     * Known limits: when device size changes by zoom in and out (responsive from PC <=> Tablet <=> Mobile)
     * we cannot handling this.
     *
     *
     */

    function __setOriginalWindowSize() {
        originalWindowSize.value.width = window.innerWidth;
        originalWindowSize.value.height = window.innerHeight;
    }

    function __windowResized() {
        if (!isMonitoring.value || !fullscreenEnabled.value || !isFullscreen.value) {
            resize_handling.value = false;
            return;
        }
        try {
            exitFullscreen().then(() => {
                if (global_methods.isFirefox()) {
                    __setOriginalWindowSize();
                }
                resize_handling.value = false;
            });
        } catch (error) {
            resize_handling.value = false;
        }
    }

    // ensure resize event is listening once at a time (not handling multiple events at the same time)
    const resize_handling = ref(false);

    useEventListener('resize', () => {

        if (resize_handling.value) {
            // console.log('resize is handling now');
            return;
        } else {
            resize_handling.value = true;
            setTimeout(() => {

                // current window size
                if(global_methods.isFirefox()){
                    windowSize.value = {
                        width: window.innerWidth,
                        height: window.innerHeight,
                    };
                }else{
                    windowSize.value = {
                        width: window.outerWidth,
                        height: window.outerHeight,
                    };
                }


                if (windowResizeTimeout) {
                    clearTimeout(windowResizeTimeout);
                }

                if (!isMonitoring.value || !fullscreenEnabled.value || !isFullscreen.value) {
                    resize_handling.value = false;
                    return;
                }

                if (global_methods.isFirefox()) {


                    // this is actually not triggering by Firefox because firefox trigger full screen change event. This event will never catches.
                    // However, below line will catches zoom changes in firefox.
                    if (originalWindowSize.value.width == windowSize.value.width && originalWindowSize.value.height == windowSize.value.height) {
                        // console.log('firefox full screen exits')
                        windowResizeTimeout = setTimeout(__windowResized, options.fullscreenDelayAfterResize);

                    } else {
                        // console.log('Firefox zoom event detected')
                        resize_handling.value = false;
                    }
                } else {
                    // Chrome and Safari will come to check if the user exits out of full screen mode.

                    // console.debug('resize event detected');
                    if (fullscreenSize.value.width !== windowSize.value.width || fullscreenSize.value.height !== windowSize.value.height) {
                        windowResizeTimeout = setTimeout(__windowResized, options.fullscreenDelayAfterResize);
                    } else {
                        resize_handling.value = false;
                    }
                }

            }, options.fullscreenDelayBeforeResize)
        }
    }, {passive: true});


    function haltWithError(type) {

        // console.debug('haltWithError');
        let reason = 'We detected you leaving the test';
        let message = 'Click on the button below to continue your Test';

        if (type === MonitorBrowserError.FULL_SCREEN_ERROR) {
            reason = 'You have left full screen';
        }

        const error = {
            type: type,
            error_code: 'test_error_alert_detected_you_left_test',
            reason: reason,
            message: message,
        };

        // isHalted.value = true;
        store.dispatch('Monitoring/haltMonitoring', error);
        // options.onError?.(error);
    }

    function maybeHaltOnFocused() {

        if (!isMonitoring.value || isHalted.value) {
            // console.debug('maybeHaltOnFocused - return isHalted.value = true');
            return;
        }

        if (!focusedEnabled.value || isFocused.value) {
            // console.debug('maybeHaltOnFocused - return isFocused.value = true');
            if (options.focusedTimeoutReset) {
                focusedCount = 0;
                focusedTimeout.value = 0;
            }
            // isHalted.value = false;
            store.dispatch('Monitoring/unHaltMonitoring')
            return;
        }

        focusedCount += 1;
        const interval = 100;
        const elapsed = interval * focusedCount;
        const timeout = focusedDelay.value * 1000;
        focusedTimeout.value = Math.min(Math.round(elapsed / timeout * 100), 100);

        if (focusedTimeout.value < 100) {
            useTimeoutFn(() => {
                // console.debug('maybeHaltOnFocused - recursive call');
                maybeHaltOnFocused();
            }, interval)
        } else {
            // console.debug('maybeHaltOnFocused - finish with error');

            haltWithError(MonitorBrowserError.FOCUS_LOST);
            focusedCount = 0;
            focusedTimeout.value = 0;
            focusedDelay.value = options.focusedDelayAfterError;
            logFocusLost().then(() => {
            });
        }
    }

    function maybeHaltOnOutside() {
        // console.debug('maybeHaltOnOutside');
        if (!isMonitoring.value || isHalted.value) {
            return;
        }

        if (!outsideEnabled.value || !isOutside.value) {
            if (options.outsideTimeoutReset) {
                outsideCount = 0;
                outsideTimeout.value = 0;
            }
            // isHalted.value = false;
            store.dispatch('Monitoring/unHaltMonitoring')
            return;
        }

        outsideCount += 1;
        const interval = 100;
        const elapsed = interval * outsideCount;
        const timeout = outsideDelay.value * 1000;
        outsideTimeout.value = Math.min(Math.round(elapsed / timeout * 100), 100);

        if (outsideTimeout.value < 100) {
            useTimeoutFn(() => {
                maybeHaltOnOutside();
            }, interval)
        } else {

            haltWithError(MonitorBrowserError.CURSOR_LOST);
            outsideCount = 0;
            outsideDelay.value = options.outsideDelayAfterError;
            logCursorLost().then(() => {
            });
        }
    }

    function startMonitoring() {
        // isHalted.value = false;
        store.dispatch('Monitoring/unHaltMonitoring')
        isFocused.value = true;
        isOutside.value = false;
        focusedCount = 0;
        outsideCount = 0;
        focusedTimeout.value = 0;
        outsideTimeout.value = 0;
        if (fullscreenEnabled.value && !isFullscreen.value) {

            isFullScreenModeStarting.value = true;

            //set the original windows size before entering the full screen
            __setOriginalWindowSize();
            enterFullscreen().then(() => {
                //set full screen size after entering the full screen mode (give enough time to set full screen)

                setTimeout(() => {
                    // console.log('set full screen size')

                    fullscreenSize.value = windowSize.value;
                    store.dispatch('Monitoring/setFullScreenEnabled', true)

                    isMonitoring.value = true;
                    isFullScreenModeStarting.value = false;
                }, options.fullscreenDelayAfterEnter + 50);
            }).catch(error => {
                haltWithError(MonitorBrowserError.FULL_SCREEN_ERROR);
            });

        } else {
            fullscreenSize.value = windowSize.value;
            isMonitoring.value = true;
            store.dispatch('Monitoring/setFullScreenEnabled', false)
        }
    }

    function resumeMonitoring() {
        startMonitoring();
    }

    function stopMonitoring() {
        isMonitoring.value = false;
        // isHalted.value = false;
        store.dispatch('Monitoring/unHaltMonitoring')
        focusedTimeout.value = 0;
        outsideTimeout.value = 0;
        // console.debug('stop monitoring')
        if (fullscreenEnabled.value) {
            exitFullscreen().then(() => {
                // console.debug('exit full screen')
            });
        }
    }


    return {
        fullscreenEnabled,
        focusedEnabled,
        visibleEnabled,
        outsideEnabled,
        focusedDelay,
        focusedTimeout,
        outsideDelay,
        outsideTimeout,
        isMonitoring,
        isHalted,
        isFocused,
        isVisible,
        isOutside,
        isFullscreen,
        isFullscreenSupported,
        enterFullscreen,
        exitFullscreen,
        toggleFullscreen,
        browserEvents,
        startMonitoring,
        resumeMonitoring,
        stopMonitoring,
    }
}